import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Table, Badge, ProgressBar, Form, InputGroup } from "react-bootstrap";
import { FaSearch, FaUserSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import UserInitialsAvatar from "../../../utils/userInitialAvatar";
import { getFullName } from "../../../utils/helpers";

const DirectoryTab = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const { directory, allocations, isLoading } = useSelector((state) => state.timeDistribution);

  const processedDirectory = useMemo(() => {
    if (!directory) return [];

    const employeeMap = new Map();

    (allocations || []).forEach((proj) => {
      const isFinanced = proj.is_financed;

      if (isFinanced) {
        const members = proj.resources || [];

        members.forEach((m) => {
          const userId = m.id || m.user_id;
          
          if (userId) {
            const stringId = String(userId);

            if (!employeeMap.has(stringId)) {
              employeeMap.set(stringId, {
                financedProjects: [],
                financedEti: 0
              });
            }

            const empData = employeeMap.get(stringId);

            const projName = proj.name || t('components.directoryTab.default_project');
            if (!empData.financedProjects.includes(projName)) {
              empData.financedProjects.push(projName);
            }

            const projectSpecificEti = Number(m.allocated ?? m.monthly_etis ?? 0);
            empData.financedEti += projectSpecificEti;
          }
        });
      }
    });

    return directory
      .map((emp) => {
        const fullName = getFullName(emp);
        
        const mappedData = employeeMap.get(String(emp.id)) || {
          financedProjects: [],
          financedEti: 0
        };

        return {
          ...emp,
          displayName: fullName,
          roleDisplay: emp.role || t('components.directoryTab.default_role'),
          financedProjects: mappedData.financedProjects,
          financedEti: Number(mappedData.financedEti.toFixed(2)),
        };
      })
      .filter((emp) => emp.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => b.financedEti - a.financedEti);
  }, [directory, allocations, searchTerm, t]);

  if (isLoading && (!directory || directory.length === 0)) {
    return <div className="p-5 text-center txt-subtle">{t('components.directoryTab.loading')}</div>;
  }

  return (
    <div className="fade-in">
      <div className="d-flex justify-content-end mb-3">
        <InputGroup size="sm" style={{ width: "300px" }} className="shadow-sm">
          <InputGroup.Text className="bg-surface border-end-0">
            <FaSearch className="txt-subtle" size={12} />
          </InputGroup.Text>
          <Form.Control 
            type="text" 
            placeholder={t('components.directoryTab.search_placeholder')} 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-start-0 ps-0 bg-surface"
            style={{ fontSize: "0.85rem" }}
          />
        </InputGroup>
      </div>

      <Card className="atlar-card overflow-hidden border-0">
        <Table hover responsive align="middle" className="mb-0">
          <thead className="bg-surface txt-subtle text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
            <tr>
              <th className="py-3 px-4">{t('components.directoryTab.table.employee')}</th>
              <th className="py-3 px-3">{t('components.directoryTab.table.financed_projects')}</th>
              <th className="py-3 px-3">{t('components.directoryTab.table.financed_eti')}</th>
              <th className="py-3 px-4 text-end">{t('components.directoryTab.table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {processedDirectory.length > 0 ? (
              processedDirectory.map((emp, index) => {
                const eti = emp.financedEti;
                
                let statusBadge, rowClass;
                
                if (eti > 1.0) {
                  statusBadge = <Badge bg="danger" className="px-2 py-1 fw-semibold" style={{ backgroundColor: 'var(--danger-red)' }}>{t('components.directoryTab.badges.exceeded')}</Badge>;
                  rowClass = "table-danger bg-opacity-10 border-bottom";
                } else if (eti === 1.0) {
                  statusBadge = <Badge bg="success" className="px-2 py-1 fw-semibold" style={{ backgroundColor: 'var(--success-green)' }}>{t('components.directoryTab.badges.at_limit')}</Badge>;
                  rowClass = "border-bottom";
                } else if (eti > 0) {
                  statusBadge = <Badge bg="success" className="px-2 py-1 fw-semibold" style={{ backgroundColor: 'var(--success-green)' }}>{t('components.directoryTab.badges.ok')}</Badge>;
                  rowClass = "border-bottom";
                } else {
                  statusBadge = <Badge bg="secondary" className="px-2 py-1 fw-semibold">{t('components.directoryTab.badges.unallocated')}</Badge>;
                  rowClass = "border-bottom bg-surface bg-opacity-50";
                }

                return (
                  <tr key={emp.id || index} className={rowClass}>
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-center gap-3">                        
                        <UserInitialsAvatar 
                          first_name={emp.first_name} 
                          last_name={emp.last_name} 
                          style={{ width: "38px", height: "38px", fontSize: "0.85rem", flexShrink: 0 }} 
                        />
                        
                        <div>
                          <div className="fw-bold txt-main">{emp.displayName}</div>
                          <div className="txt-subtle" style={{ fontSize: "0.75rem" }}>{emp.roleDisplay}</div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {emp.financedProjects.length > 0 ? (
                          emp.financedProjects.map((projName, pIdx) => (
                            <Badge key={pIdx} bg="light" text="dark" className="border fw-normal">
                              ● {projName}
                            </Badge>
                          ))
                        ) : (
                          <span className="txt-subtle" style={{ fontSize: "0.80rem" }}>{t('components.directoryTab.badges.none')}</span>
                        )}
                      </div>
                    </td>

                    <td style={{ width: "250px" }}>
                      <div className="d-flex align-items-center gap-2">
                        <ProgressBar 
                          now={Math.min(eti * 100, 100)} 
                          style={{ height: "6px", backgroundColor: 'var(--gray-200)' }} 
                          variant={eti > 1.0 ? "danger" : eti === 1.0 ? "success" : "success"}
                        />
                        <span className={`small fw-semibold ${eti > 1.0 ? 'text-danger' : 'text-success'}`}>
                          {eti.toFixed(2)} / 1.00
                        </span>
                      </div>
                    </td>

                    <td className="text-end px-4">
                      {statusBadge}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-5">
                  <FaUserSlash size={32} className="txt-subtle opacity-50 mb-3" />
                  <h6 className="txt-main fw-semibold">{t('components.directoryTab.empty_search.title')}</h6>
                  <p className="txt-subtle small mb-0">{t('components.directoryTab.empty_search.subtitle')}</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    </div>
  );
};

export default DirectoryTab;